import { useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { NavLink, matchPath, useLocation, Link as RouterLink } from 'react-router-dom'
import { Badge, Grid, Skeleton } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { UserRole } from '@common/interfaces/user'
import cn from 'classnames'
import SmallLogo from '../../assets/images/D-logo.svg?react'
import Logo from '../../assets/images/client-intake/dwight-black-logo.svg?react'
import { ROUTES } from '../../constants/routes'
import { countAsUnreadIndicator, toUnreadBadgeProps } from '../../helpers/unreadBadge'
import { ATLAS_PREVIEW_OPT_OUT } from '../../hooks/useGlobalAtlasLinkPreview'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useLastTaskTab } from '../../hooks/useLastTaskTab'
import { UnreadIndicator } from '../../redux/channels/unreadIndicator'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Text from '../Common/Text'
import styles from './SideMenu.module.scss'
import {
  SIDE_MENU,
  IMenuData,
  MenuBadge,
  SIDE_MENU_PAST_CLIENT_USER,
  SIDE_MENU_CLIENT_USER,
} from './SideMenuConfig'
import ExpandableMenuItem from './components/ExpandableNavItem'
import { PropsFromRedux } from './index'
import { getInitialExpandableListItemsState, updateExpandableListItemsState } from './utils'

const HOVER_EXPAND_DELAY = 150
const HOVER_COLLAPSE_DELAY = 150

export type IProps = PropsFromRedux

const SideMenu = ({
  role = UserRole.CLIENT_USER,
  onLogout,
  toggleSidebar,
  isSidebarOpen,
  isPastClient,
  isDueDiligenceClient,
  isAdminLoggedAsClient,
  isClientUser,
  totalUnreadIndicator,
  unreadTaskCount,
  favoriteProjects,
}: IProps) => {
  const location = useLocation()
  const isMobile = useIsMobile()
  const [expandableListItemsState, setExpandableListItemsState] = useState(
    getInitialExpandableListItemsState(role, location),
  )
  const [isHoverExpanded, setIsHoverExpanded] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Resolves a menu item's declared `badge` to its live count. The Messages
  // sidebar distinguishes mentions from unread volume with a separate badge, but
  // the nav rail is ~40px wide collapsed — two competing counters there read as
  // noise, so mentions fold into the one total. Mentions are always a subset of
  // unread, so a mention can never leave the nav showing zero.
  const badgeIndicators: Record<MenuBadge, UnreadIndicator> = {
    unreadMessages: totalUnreadIndicator,
    unreadTasks: countAsUnreadIndicator(unreadTaskCount),
  }

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = undefined
    }
  }, [])

  useEffect(() => clearHoverTimer, [clearHoverTimer])

  const handleMouseEnter = useCallback(() => {
    clearHoverTimer()
    hoverTimerRef.current = setTimeout(() => setIsHoverExpanded(true), HOVER_EXPAND_DELAY)
  }, [clearHoverTimer])

  const handleMouseLeave = useCallback(() => {
    clearHoverTimer()
    hoverTimerRef.current = setTimeout(() => setIsHoverExpanded(false), HOVER_COLLAPSE_DELAY)
  }, [clearHoverTimer])

  const handlePinToggle = useCallback(() => {
    // When unpinning, stay expanded until the cursor leaves the menu
    if (isSidebarOpen) {
      setIsHoverExpanded(true)
    }
    toggleSidebar()
  }, [isSidebarOpen, toggleSidebar])

  const isExpanded = isMobile ? isSidebarOpen : isSidebarOpen || isHoverExpanded

  const handleItemExpand = useCallback(
    (itemName: string) => {
      const newState = updateExpandableListItemsState(expandableListItemsState, itemName)
      setExpandableListItemsState(newState)
    },
    [expandableListItemsState],
  )

  const handleLogout = useCallback(async () => {
    await onLogout()
  }, [onLogout])

  const sideMenuItems = useMemo(() => {
    if (role) {
      if (role === UserRole.CLIENT_USER) {
        if (isPastClient) {
          return SIDE_MENU_PAST_CLIENT_USER.filter(
            (item) => !isAdminLoggedAsClient || item.link !== ROUTES.SETTINGS,
          )
        }
        if (isPastClient !== null) {
          return SIDE_MENU_CLIENT_USER.filter(
            (item) => !isAdminLoggedAsClient || item.link !== ROUTES.SETTINGS,
          )
        }
      }
      return SIDE_MENU[role]
    }
  }, [role, isPastClient, isAdminLoggedAsClient])

  // The Tasks item points at whichever tab this browser was last on rather than
  // always My Tasks. Resolved here rather than by a redirect route because the
  // menu config is a static array and this is the only item whose destination
  // moves — favorites are seeded app-wide in MainLayout, so the answer is
  // already in the store and needs no fetch of its own. Falls back to My Tasks
  // until they land, and whenever the remembered project is no longer a tab.
  const lastTaskTab = useLastTaskTab(favoriteProjects.data ?? [])

  const menuItems = useMemo(
    () =>
      sideMenuItems?.map((item) =>
        item.key === ROUTES.MY_TASKS ? { ...item, link: lastTaskTab } : item,
      ),
    [sideMenuItems, lastTaskTab],
  )

  const isCurrentPage = useCallback(
    (link: string) => {
      return !!matchPath(
        {
          path: link,
          end: true,
        },
        location.pathname,
      )
    },
    [location],
  )

  if (role === UserRole.CLIENT_USER && isDueDiligenceClient) {
    return null
  }
  return (
    <>
      <Drawer
        {...ATLAS_PREVIEW_OPT_OUT}
        data-preload-routes
        className={cn(styles.drawer, {
          [styles.expanded]: isExpanded,
          [styles.collapsed]: !isExpanded,
          [styles.pinned]: !isMobile && isSidebarOpen,
          [styles.hoverExpanded]: !isMobile && isExpanded && !isSidebarOpen,
        })}
        variant={isMobile ? 'temporary' : 'permanent'}
        classes={{
          paper: styles.drawerPaper,
        }}
        anchor={isMobile ? 'top' : 'left'}
        open={isSidebarOpen}
        onClose={toggleSidebar}
        onMouseEnter={isMobile ? undefined : handleMouseEnter}
        onMouseLeave={isMobile ? undefined : handleMouseLeave}
        ModalProps={{
          keepMounted: !isMobile, // Only keep mounted on desktop
        }}
        hideBackdrop={!isMobile || (isMobile && !isSidebarOpen)}
        slotProps={{
          backdrop:
            isMobile && !isSidebarOpen
              ? undefined // Don't render backdrop at all when closed on mobile
              : { invisible: !isSidebarOpen },
        }}
      >
        <List
          component="nav"
          classes={{
            root: styles.wrapper,
          }}
        >
          <div className={styles.menuHeader}>
            <Link component={RouterLink} to={ROUTES.HOMEPAGE} className={styles.logoWrapper}>
              <Logo className={cn(styles.logo, styles.logoFull)} />
              <SmallLogo className={cn(styles.logo, styles.logoSmall)} />
            </Link>
            {!isMobile && (
              <IconButton
                tooltipPlacement="right"
                title={isSidebarOpen ? 'Unpin menu' : 'Pin menu open'}
                className={styles.pinButton}
                onClick={handlePinToggle}
                disableRipple
                data-cy="sidebar-pin-button"
              >
                <Icon name="sidebar-pin" size="sm" />
              </IconButton>
            )}
          </div>
          <div className={styles.sideMenuItems}>
            {!sideMenuItems.length ? (
              <>
                <Grid container className={styles.skeletonWrapper}>
                  {Array.from({ length: 5 }).map((_, key) => (
                    <>
                      <Grid key={key} size={isExpanded ? 3 : 12}>
                        <Skeleton
                          variant="rectangular"
                          width={30}
                          height={30}
                          className={styles.skeleton}
                        />
                      </Grid>
                      {isExpanded && (
                        <Grid key={key} size={9}>
                          <Skeleton
                            variant="rectangular"
                            width={150}
                            height={30}
                            className={styles.skeleton}
                          />
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>
              </>
            ) : (
              (menuItems || []).map(
                ({
                  icon,
                  label,
                  subItems,
                  link,
                  activeIcon,
                  divider,
                  badge,
                  activeRoutes,
                }: IMenuData) => (
                  <div key={link} className={styles.sideMenuItem}>
                    {subItems ? (
                      <ExpandableMenuItem
                        icon={icon}
                        label={label}
                        link={link}
                        subItems={subItems}
                        handleItemExpand={handleItemExpand}
                        itemExpanded={expandableListItemsState[label]}
                        sidebarExpanded={isExpanded}
                        location={location}
                        toggleSidebar={isMobile ? toggleSidebar : undefined}
                      />
                    ) : (
                      <>
                        {divider && <div className={styles.divider} />}
                        <NavLink
                          to={link}
                          className={({ isActive }) =>
                            isActive || activeRoutes?.some(isCurrentPage) ? styles.active : ''
                          }
                          onClick={() => {
                            if (isMobile && toggleSidebar) {
                              toggleSidebar()
                            }
                          }}
                        >
                          <ListItemButton
                            className={styles.navItem}
                            disableRipple
                            data-cy={`menu-item-${label}`}
                          >
                            <ListItemIcon
                              className={cn(styles.icon, {
                                [styles.colorIcon]: !activeIcon,
                              })}
                            >
                              {/* The badge rides the icon rather than sitting by
                                  the label, so the count survives the collapsed
                                  rail — where the label isn't rendered at all. */}
                              <Badge
                                {...toUnreadBadgeProps(
                                  badge ? badgeIndicators[badge] : { kind: 'none' },
                                )}
                                color="error"
                                max={99}
                                classes={{ badge: styles.navBadge }}
                              >
                                {activeIcon && isCurrentPage(link) ? activeIcon : icon}
                              </Badge>
                            </ListItemIcon>
                            <ListItemText
                              classes={{
                                root: styles.labelRoot,
                                primary: styles.label,
                              }}
                              primary={label}
                            />
                          </ListItemButton>
                        </NavLink>
                      </>
                    )}
                  </div>
                ),
              )
            )}
          </div>
          {isClientUser && !isAdminLoggedAsClient && (
            <div className={styles.profileWrapper}>
              <div onClick={handleLogout} className={styles.navItem}>
                <div className={styles.icon}>
                  <Icon name="logout" size="md" />
                </div>
                <Text variant="body" strong tone="secondary" className={styles.signOutLabel}>
                  Sign out
                </Text>
              </div>
            </div>
          )}
        </List>
      </Drawer>
      <div className={cn({ [styles.mobileHeaderSpacer]: isMobile })} />
    </>
  )
}

export default SideMenu
